import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { AuthMode, BrandingView, ThemeId } from "@themislex/shared";

interface BrandingState {
  theme: ThemeId;
  firmName: string;
  authMode: AuthMode;
}

interface ThemeContextValue extends BrandingState {
  setTheme: (theme: ThemeId) => void;
}

const DEFAULTS: BrandingState = {
  theme: "chambers",
  firmName: "ThemisLex Chambers",
  authMode: "relief",
};

const ThemeContext = createContext<ThemeContextValue>({
  ...DEFAULTS,
  setTheme: () => undefined,
});

const CACHE_KEY = "tl.branding";

function readCache(): BrandingState {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return { ...DEFAULTS, ...(JSON.parse(cached) as Partial<BrandingState>) };
  } catch {
    /* ignore malformed cache */
  }
  return DEFAULTS;
}

/**
 * The active theme lives in the single-row firm_settings table (D1) and is
 * served unauthenticated by GET /api/public/branding so the login screen is
 * themed too. A localStorage cache avoids a flash of the wrong theme.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BrandingState>(readCache);

  useEffect(() => {
    document.documentElement.dataset.theme = state.theme;
  }, [state.theme]);

  useEffect(() => {
    let alive = true;
    fetch("/api/public/branding")
      .then((res) => (res.ok ? res.json() : null))
      .then((branding: BrandingView | null) => {
        if (!alive || !branding) return;
        const next: BrandingState = {
          theme: branding.theme,
          firmName: branding.firmName,
          authMode: branding.authMode,
        };
        setState(next);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const setTheme = useCallback((theme: ThemeId) => {
    setState((prev) => {
      const next = { ...prev, theme };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ ...state, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
